import React, { FC, useEffect, useState } from 'react';
import { features } from 'texts/texts';
import { TextWithStyle } from 'components/TextWithStyle';
import { SEO } from 'components/seo/SEO';
import { breadcrumbJsonLd } from 'components/seo/organizationSchema';
import routeManifest from 'routes/routeManifest.json';
import {
  ChangelogEntry,
  ChangelogItemType,
  ChangelogPlatform,
  fetchChangelog,
  formatReleaseMonth,
  releaseMonthIso,
} from './changelogData';
import { legacyChangelog } from './legacyChangelog';
import '../Features.css';
import './Changelog.css';

const route = routeManifest.find((r) => r.path === '/features/changelog')!;
const breadcrumbs = breadcrumbJsonLd([
  { name: 'Home', path: '/' },
  { name: 'Features', path: '/features' },
  { name: "What's New", path: '/features/changelog' },
]);

const typeLabels: Record<ChangelogItemType, string> = {
  new_feature: 'New',
  improvement: 'Improved',
  bug_fix: 'Fixed',
};

const typeOrder: ChangelogItemType[] = ['new_feature', 'improvement', 'bug_fix'];

const platformLabels: Record<ChangelogPlatform, string | null> = {
  all: null,
  ios: 'iOS',
  android: 'Android',
};

export const Changelog: FC = () => {
  const [entries, setEntries] = useState<ChangelogEntry[] | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    fetchChangelog()
      .then(setEntries)
      .catch(() => setHasError(true));
  }, []);

  // Database entries first, then the hardcoded App Store history (v1.3.2 and earlier).
  // Wait for the fetch so older versions don't flash at the top first; if it fails,
  // still show the older history. A version in both places uses the database copy.
  const shownEntries: ChangelogEntry[] =
    entries === null
      ? hasError ? legacyChangelog : []
      : [...entries, ...legacyChangelog.filter((legacy) => !entries.some((e) => e.version === legacy.version))];

  return (
    <div className="features-content-container">
      <SEO title={route.title} description={route.description} path={route.path} jsonLd={[breadcrumbs]} />
      <div className="features-header">
        <h1>
          {features.changelog.title.map((part, index) => (
            <TextWithStyle key={index} text={part.text} style={part.style} />
          ))}
        </h1>
        <p>{features.changelog.description}</p>
      </div>

      {hasError && (
        <p className="features-empty">
          We couldn't load the latest updates. Please try again soon 🌱
        </p>
      )}
      {!hasError && entries === null && <p className="features-empty">Loading updates…</p>}

      <div className="changelog-list">
        {shownEntries.map((entry) => (
          <article key={entry.id} className="changelog-entry">
            <header className="changelog-entry-header">
              <h2>Version {entry.version}</h2>
              <time dateTime={releaseMonthIso(entry.release_month)}>{formatReleaseMonth(entry.release_month)}</time>
            </header>
            {typeOrder.map((type) => {
              // Newest first within each section, matching the app
              const items = entry.changelog_entry_items
                .filter((item) => item.type === type)
                .sort((a, b) => b.id - a.id);
              if (items.length === 0) return null;
              return (
                <section key={type} className="changelog-section">
                  <h3 className={`changelog-badge changelog-badge-${type}`}>{typeLabels[type]}</h3>
                  <ul className="changelog-items">
                    {items.map((item) => (
                      <li key={item.id}>
                        {item.text}
                        {platformLabels[item.platform] && (
                          <span className="changelog-platform">{platformLabels[item.platform]}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </article>
        ))}
      </div>
    </div>
  );
};
